import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { geocodeSearch, reverseGeocode, type GasGeocodeSuggestion } from '../../shared/kit/geo/geocode';
import {
    GasAccordion,
    GasAccordionItem,
    GasButton,
    useGasI18n,
    useGasNlSearch,
    type NlFilters,
} from '../../shared/kit';
import {
    GasGeoMap,
    type GasGeoPoint,
    type GasGeoViewport,
    type MapRef,
} from '../../shared/kit/geo';
import { directoryGet } from './api';
import {
    trackDirectoryPageView,
    trackDirectorySearch,
    trackMemberClick,
} from './analytics';
import type {
    CachedMarkersResponse,
    DirectorySuggestionItem,
    DirectoryFacetField,
    FacetSuggestionItem,
    FilterOption,
    InitData,
    MarkerData,
    SearchResultsResponse,
    SearchState,
    SpecialisationCategory,
    SuggestionsResponse,
} from './types';
import {
    buildMemberOrder,
    buildFacetSuggestions,
    countUniqueMembers,
    deriveDirectoryResultsState,
    memberInitials,
    memberKey,
    selectViewportMembers,
    normalizeDirectoryMarkers,
} from './utils';

interface AppProps {
    initData: InitData;
}

function useWideDirectoryLayout(): boolean {
    const query = '(min-width: 1200px)';
    const [matches, setMatches] = useState(() => typeof window !== 'undefined' && window.matchMedia?.(query).matches === true);

    useEffect(() => {
        if (!window.matchMedia) return undefined;
        const media = window.matchMedia(query);
        const update = () => setMatches(media.matches);
        update();
        media.addEventListener('change', update);
        return () => media.removeEventListener('change', update);
    }, []);

    return matches;
}

// Réglages effectifs après application du preset `display_style`.
// Les presets surchargent uniquement les champs d'**apparence visuelle**
// (marker_style, card_density, sort_order) et laissent intacts les seuils
// numériques (zooms, clusters, max_visible_results) configurés par l'admin
// dans site-config > Annuaire. Cela évite qu'un preset écrase les valeurs
// fines voulues par le client.
export type ResolvedSettings = {
    marker_style: 'dot' | 'photo_zoom' | 'numbered' | 'theme_color';
    card_density: 'compact' | 'medium' | 'large';
    sort_order: 'random_stable' | 'alphabetical' | 'by_member_type' | 'by_distance';
    marker_photo_min_zoom: number;
    list_results_min_zoom: number;
    show_info_window_on_click: boolean;
    show_markers_on_load: boolean;
    cluster_max_zoom: number;
    cluster_radius: number;
    cluster_min_points: number;
    max_visible_results: number;
};

export function resolveSettings(raw: import('./types').DirectorySearchSettings | undefined): ResolvedSettings {
    const fallback: ResolvedSettings = {
        marker_style: 'photo_zoom',
        card_density: 'medium',
        sort_order: 'random_stable',
        // Photos un peu plus tôt (zoom ville/région, pas seulement ultra-serré).
        marker_photo_min_zoom: 8,
        list_results_min_zoom: 8,
        show_info_window_on_click: true,
        show_markers_on_load: true,
        cluster_max_zoom: 9,
        cluster_radius: 40,
        cluster_min_points: 3,
        max_visible_results: 50,
    };
    if (!raw) return fallback;

    // Valeurs raw du site-config, repliées champ par champ : un payload
    // partiel ne doit jamais produire de `undefined` dans les réglages
    // effectifs.
    const base: ResolvedSettings = {
        ...fallback,
        ...raw,
    };

    // Les presets ne touchent QUE l'apparence (marker_style, card_density,
    // sort_order). Les seuils numériques restent ceux du site-config.
    switch (raw.display_style) {
        case 'doctolib':
            return { ...base, marker_style: 'dot', card_density: 'compact', sort_order: 'random_stable' };
        case 'airbnb':
            return { ...base, marker_style: 'photo_zoom', card_density: 'medium', sort_order: 'by_distance' };
        case 'google_places':
            return { ...base, marker_style: 'numbered', card_density: 'compact', sort_order: 'by_distance' };
        case 'idealista':
            return { ...base, marker_style: 'theme_color', card_density: 'compact', sort_order: 'random_stable' };
        case 'hybrid':
            return { ...base, marker_style: 'photo_zoom', card_density: 'medium', sort_order: 'random_stable' };
        case 'custom':
        default:
            return base;
    }
}

const INITIAL_SEARCH: SearchState = {
    query: '',
    lieu: '',
    lat: '',
    lng: '',
    radius: '5',
    genre: '',
    langues: [],
    specialisations: [],
    champs_hypnose: [],
    publics_privilegies: [],
    pratiques_complementaires: [],
};

function hasActiveFilters(s: SearchState): boolean {
    return !!(
        s.query ||
        s.lieu ||
        s.genre ||
        s.langues.length ||
        s.specialisations.length ||
        s.champs_hypnose.length ||
        s.publics_privilegies.length ||
        s.pratiques_complementaires.length
    );
}

function mergeNlFilters(search: SearchState, filters: NlFilters): SearchState {
    const values = (field: string): string[] => {
        const raw = filters[field]?.in;
        return Array.isArray(raw)
            ? Array.from(new Set(raw.map(value => String(value)).filter(Boolean)))
            : [];
    };
    return {
        ...search,
        genre: values('genre')[0] ?? '',
        langues: values('langues'),
        specialisations: values('specialisations'),
        champs_hypnose: values('champs_hypnose'),
        publics_privilegies: values('publics_privilegies'),
        pratiques_complementaires: values('pratiques_complementaires'),
    };
}

function nlScalar(filters: NlFilters, field: string): string {
    const value = filters[field]?.eq;
    return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
}

function toggleInArray(arr: string[], v: string): string[] {
    const idx = arr.indexOf(v);
    return idx >= 0 ? arr.filter((_, i) => i !== idx) : [...arr, v];
}

function distanceKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
    const radians = (degrees: number) => degrees * Math.PI / 180;
    const deltaLat = radians(bLat - aLat);
    const deltaLng = radians(bLng - aLng);
    const latitudeA = radians(aLat);
    const latitudeB = radians(bLat);
    const value = Math.sin(deltaLat / 2) ** 2
        + Math.cos(latitudeA) * Math.cos(latitudeB) * Math.sin(deltaLng / 2) ** 2;
    return 6371 * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(Math.max(0, 1 - value)));
}

type MultiFilterField = DirectoryFacetField;

const FILTER_SECTION_BY_FIELD: Record<MultiFilterField, string> = {
    specialisations: 'specialisations',
    langues: 'langues',
    publics_privilegies: 'publics',
    champs_hypnose: 'champs',
    pratiques_complementaires: 'pratiques',
};

interface ActiveFilterChip {
    key: string;
    label: string;
    field: 'genre' | MultiFilterField;
    value: string;
}

function getFullName(m: MarkerData): string {
    if (m.prenom && m.nom) return `${m.prenom} ${m.nom}`;
    if (m.prenom_nom) return m.prenom_nom;
    return m.display_name ?? '';
}

function getStreetAddress(m: MarkerData): string {
    if (m.adresse) return m.adresse.replace(/<br\s*\/?>/gi, ' ').replace(/\s+/g, ' ').trim();
    return '';
}

function getCityLine(m: MarkerData): string {
    if (m.npa && m.localite) return `${m.npa} ${m.localite}`;
    return '';
}

interface MemberAvatarProps {
    member: MarkerData;
    alt: string;
}

const MemberAvatar: React.FC<MemberAvatarProps> = ({ member, alt }) => {
    const [failed, setFailed] = useState(false);
    const src = String(member.image_profile ?? '').trim();
    if (!src || failed) {
        return (
            <span className="gas-member-search__avatar-fallback" aria-label={alt} role="img">
                {memberInitials(member)}
            </span>
        );
    }

    return <img src={src} alt={alt} onError={() => setFailed(true)} />;
};

/** Icône GPS inline — le frontend public n’enqueue pas Tabler/FontAwesome. */
const IconCurrentLocation: React.FC<{ className?: string }> = ({ className }) => (
    <svg
        className={className}
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <circle cx="12" cy="12" r="3" />
        <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
        <circle cx="12" cy="12" r="8" />
    </svg>
);

const IconLoader: React.FC<{ className?: string }> = ({ className }) => (
    <svg
        className={className}
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        aria-hidden="true"
    >
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
);

interface MemberMapSummaryProps {
    member: MarkerData;
    profileLabel?: string;
    preview?: boolean;
    /** Libellés résolus (spécialités, langues…) pour la fiche express. */
    facetLabels?: string[];
    onProfileClick?: (member: MarkerData) => void;
}

const MemberMapSummary: React.FC<MemberMapSummaryProps> = ({
    member,
    profileLabel,
    preview = false,
    facetLabels = [],
    onProfileClick,
}) => {
    const street = getStreetAddress(member);
    const city = getCityLine(member);
    const name = getFullName(member);
    const chips = facetLabels.slice(0, preview ? 3 : 5);

    return (
        <div
            className={`gas-member-search__popup${preview ? ' gas-member-search__popup--hover' : ''}`}
            role={preview ? 'tooltip' : undefined}
        >
            <div className="gas-member-search__popup-top">
                <div className="gas-member-search__popup-avatar" aria-hidden="true">
                    <MemberAvatar member={member} alt="" />
                </div>
                <div className="gas-member-search__popup-meta">
                    <div className="gas-member-search__popup-name">{name}</div>
                    {member.nom_cabinet && (
                        <div className="gas-member-search__popup-cabinet">{member.nom_cabinet}</div>
                    )}
                    {(street || city) && (
                        <address className="gas-member-search__popup-address">
                            {street && <span>{street}</span>}
                            {city && <span>{city}</span>}
                        </address>
                    )}
                </div>
            </div>
            {chips.length > 0 && (
                <ul className="gas-member-search__popup-chips">
                    {chips.map(label => (
                        <li key={label}>{label}</li>
                    ))}
                </ul>
            )}
            {!preview && member.aph_member_url && profileLabel && (
                <a
                    className="gas-member-search__popup-link"
                    href={String(member.aph_member_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => onProfileClick?.(member)}
                >
                    {profileLabel}
                </a>
            )}
            {preview && member.aph_member_url && profileLabel && (
                <span className="gas-member-search__popup-hint">{profileLabel}</span>
            )}
        </div>
    );
};

// ──────────────────────────────────────────────────────────────────────
// Sous-composants UI
// ──────────────────────────────────────────────────────────────────────

interface OptionListProps {
    options: FilterOption[];
    selected: string[];
    onToggle: (slug: string) => void;
    counts?: Map<string, number>;
    countTitle?: (count: number) => string;
}

const OptionList: React.FC<OptionListProps> = ({ options, selected, onToggle, counts, countTitle }) => (
    <div className="available-options">
        {options.map(opt => (
            <button
                type="button"
                key={opt.slug}
                className={`option-item${selected.includes(opt.slug) ? ' selected' : ''}`}
                onClick={() => onToggle(opt.slug)}
                aria-pressed={selected.includes(opt.slug)}
                aria-label={opt.label}
                aria-description={counts !== undefined ? countTitle?.(counts.get(opt.slug) ?? 0) : undefined}
            >
                <span>{opt.label}</span>
                {counts !== undefined && (
                    <span className="gas-member-search__option-count" title={countTitle?.(counts.get(opt.slug) ?? 0)} aria-hidden="true">
                        {counts.get(opt.slug) ?? 0}
                    </span>
                )}
            </button>
        ))}
    </div>
);

interface SpecGroupProps {
    cats: SpecialisationCategory[];
    selected: string[];
    onToggle: (slug: string) => void;
    counts?: Map<string, number>;
    countTitle?: (count: number) => string;
}

const SpecGroup: React.FC<SpecGroupProps> = ({ cats, selected, onToggle, counts, countTitle }) => (
    <div className="available-options available-options--grouped">
        {cats.map(cat => (
            <div key={cat.cat_slug} className="option-group">
                <h5 className="option-group-title">{cat.cat_label}</h5>
                <div className="option-group-items">
                    {cat.options.map(opt => (
                        <button
                            type="button"
                            key={opt.slug}
                            className={`option-item${selected.includes(opt.slug) ? ' selected' : ''}`}
                            onClick={() => onToggle(opt.slug)}
                            aria-pressed={selected.includes(opt.slug)}
                            aria-label={opt.label}
                            aria-description={counts !== undefined ? countTitle?.(counts.get(opt.slug) ?? 0) : undefined}
                        >
                            <span>{opt.label}</span>
                            {counts !== undefined && (
                                <span className="gas-member-search__option-count" title={countTitle?.(counts.get(opt.slug) ?? 0)} aria-hidden="true">
                                    {counts.get(opt.slug) ?? 0}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>
        ))}
    </div>
);

// ──────────────────────────────────────────────────────────────────────
// App principal
// ──────────────────────────────────────────────────────────────────────

const App: React.FC<AppProps> = ({ initData }) => {
    const { t } = useGasI18n();
    const texts = initData.texts ?? {};
    const isMember = initData.isMember ?? false;
    const cfg = initData.config;
    const filterOptions = initData.filterOptions;

    // Analytics : vue page carte (dédup session 30 min côté tracker).
    useEffect(() => {
        trackDirectoryPageView(cfg.rest_base_url, 'map', cfg.rest_nonce, 'map_page');
    }, [cfg.rest_base_url, cfg.rest_nonce]);

    const optionLabels = useMemo(() => {
        const labels = new Map<string, string>();
        const add = (field: MultiFilterField, options: FilterOption[]) => {
            options.forEach(option => labels.set(`${field}:${option.slug}`, option.label));
        };
        add('langues', filterOptions.langues ?? []);
        add('champs_hypnose', filterOptions.champs_hypnose ?? []);
        add('publics_privilegies', filterOptions.publics_privilegies ?? []);
        add('pratiques_complementaires', filterOptions.pratiques_complementaires ?? []);
        add('specialisations', (filterOptions.specialisationsByCat ?? []).flatMap(category => category.options));
        return labels;
    }, [filterOptions]);

    const suggestionGroupLabels = useMemo(() => ({
        specialisations: texts['specializations_title'] ?? t('membre.search.defaults.specializations_title'),
        langues: texts['languages_title'] ?? t('membre.search.defaults.languages_title'),
        champs_hypnose: texts['fields_title'] ?? t('membre.search.defaults.fields_title'),
        publics_privilegies: t('membre.search.ui.publics_title'),
        pratiques_complementaires: t('membre.search.ui.pratiques_title'),
    }), [texts, t]);

    // Résout les réglages d'apparence/tri configurés en
    // site-config > Annuaire. Mémoïsé pour ne pas recalculer à chaque
    // re-render — le preset peut imposer marker_style, card_density, etc.
    const settings = useMemo(() => resolveSettings(cfg.directory_search), [cfg.directory_search]);

    // ── Search state ───────────────────────────────────────────────────
    const [search, setSearch] = useState<SearchState>(INITIAL_SEARCH);
    /** Panneau capsule « configurer la recherche » (solution Airbnb + critères in-popup). */
    const [showConfig, setShowConfig] = useState(false);
    const [configFocus, setConfigFocus] = useState<'who' | 'where' | 'prefs' | null>(null);
    const configPanelRef = useRef<HTMLDivElement | null>(null);
    const capsuleBarRef = useRef<HTMLDivElement | null>(null);
    const whoInputRef = useRef<HTMLInputElement | null>(null);
    const whereInputRef = useRef<HTMLInputElement | null>(null);
    // Spécialisations fermées par défaut : listes très longues dans la popup
    // (surtout mobile). Ouverture via raccourci / recherche / openCriteria.
    const [openFilterSections, setOpenFilterSections] = useState<string[]>(['langues']);
    const [filterQuery, setFilterQuery] = useState('');
    const [pendingFilterSection, setPendingFilterSection] = useState<string | null>(null);
    const [searchActive, setSearchActive] = useState(false);
    const [naturalLocationError, setNaturalLocationError] = useState('');
    const isWideDirectoryLayout = useWideDirectoryLayout();
    const naturalSearch = useGasNlSearch({
        source: 'member_directory',
        restUrl: cfg.rest_base_url,
        nonce: cfg.rest_nonce,
        returnResults: false,
        debounceMs: false,
    });
    const clearNaturalSearch = naturalSearch.clear;

    const activeFilterChips = useMemo<ActiveFilterChip[]>(() => {
        const chips: ActiveFilterChip[] = [];
        if (search.genre) {
            chips.push({
                key: `genre:${search.genre}`,
                label: search.genre === 'femme' ? texts['genre_femme'] : texts['genre_homme'],
                field: 'genre',
                value: search.genre,
            });
        }
        const fields: MultiFilterField[] = [
            'langues',
            'specialisations',
            'champs_hypnose',
            'publics_privilegies',
            'pratiques_complementaires',
        ];
        fields.forEach(field => {
            search[field].forEach(value => chips.push({
                key: `${field}:${value}`,
                label: optionLabels.get(`${field}:${value}`) ?? value,
                field,
                value,
            }));
        });
        return chips;
    }, [optionLabels, search, texts]);

    // ── Carte / résultats ──────────────────────────────────────────────
    const [markers, setMarkers] = useState<MarkerData[]>([]);
    const [facetUniverse, setFacetUniverse] = useState<MarkerData[]>([]);
    const [statusCount, setStatusCount] = useState<number | null>(null);
    const [statusMessage, setStatusMessage] = useState<string>('');
    const [viewport, setViewport] = useState<{ bbox: [number, number, number, number]; zoom: number } | null>(null);

    // Trace si le visiteur a interagi avec la carte (pan/zoom). Tant que
    // false, on respecte `show_markers_on_load` : si désactivé, aucun
    // marker n'apparaît jusqu'à la première interaction. Dès qu'on bouge
    // la carte, on bascule à true et le filtre redevient normal (basé
    // uniquement sur le zoom courant >= list_results_min_zoom).
    const [userInteracted, setUserInteracted] = useState(false);
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    /**
     * Fiche express collante (overlay carte).
     * Ouverture au **clic** marqueur / résultat — pas au survol (sinon impossible
     * d’atteindre « Voir le profil » : le leave/blur refermait la fiche).
     * Fermeture : ×, Escape, ou sélection d’un autre membre.
     */
    const [expressMember, setExpressMember] = useState<MarkerData | null>(null);
    const [geolocating, setGeolocating] = useState(false);
    const [mobileView, setMobileView] = useState<'map' | 'list'>('map');

    // ── Suggestions ────────────────────────────────────────────────────
    const [nameSuggestions, setNameSuggestions] = useState<DirectorySuggestionItem[]>([]);
    const [showNameSuggestions, setShowNameSuggestions] = useState(false);
    const [placeSuggestions, setPlaceSuggestions] = useState<GasGeocodeSuggestion[]>([]);
    const [showPlaceSuggestions, setShowPlaceSuggestions] = useState(false);

    const mapRef = useRef<MapRef | null>(null);
    const nameSuggestTimer = useRef<number | null>(null);
    const placeSuggestTimer = useRef<number | null>(null);
    const placeSuggestAbort = useRef<AbortController | null>(null);
    const filterPanelRefs = useRef<Record<string, HTMLDivElement | null>>({});

    const hasGeolocation = typeof navigator !== 'undefined' && 'geolocation' in navigator;

    // Bounding box configurée dans l'onglet "Géolocalisation" du site-config
    // (helper PHP `gas_geo_map_bounds()`). On l'utilise comme cadrage par
    // défaut au chargement et au reset : `fitBounds()` adapte le zoom à la
    // taille du container, conformément à l'intention métier.
    const homeBounds = useMemo<[[number, number], [number, number]]>(() => {
        const b = cfg.geo.bounds ?? { north: 47.8, south: 45.8, east: 10.5, west: 5.9 };
        return [
            [Number(b.west), Number(b.south)],
            [Number(b.east), Number(b.north)],
        ];
    }, [cfg.geo.bounds]);

    const countries = useMemo(
        () => (cfg.geo.countries ?? []).map(country => country.code.toLowerCase()),
        [cfg.geo.countries],
    );

    // Points de carte au format générique GasGeoPoint. L'id est suffixé par
    // l'index : un membre peut avoir plusieurs adresses (donc plusieurs
    // marqueurs) — l'identité « membre » est recalculée via memberKey(data)
    // pour la synchronisation hover carte ↔ liste.
    const mapPoints = useMemo<GasGeoPoint<MarkerData>[]>(() => markers
        .filter(m => m.lat != null && m.lng != null)
        .map((m, i) => ({
            id: `${memberKey(m)}#${i}`,
            lat: Number(m.lat),
            lng: Number(m.lng),
            label: getFullName(m),
            data: m,
        })), [markers]);

    const pointById = useMemo(
        () => new Map(mapPoints.map(p => [p.id, p])),
        [mapPoints],
    );

    const memberOrder = useMemo(() => buildMemberOrder(markers), [markers]);

    const facetCounts = useMemo(() => {
        const counts = new Map<MultiFilterField, Map<string, number>>();
        const fields = Object.keys(FILTER_SECTION_BY_FIELD) as MultiFilterField[];
        const normalizedQuery = search.query.trim().toLocaleLowerCase();
        const searchLat = Number(search.lat);
        const searchLng = Number(search.lng);
        const radius = Number(search.radius);
        const hasGeoFilter = Number.isFinite(searchLat) && Number.isFinite(searchLng)
            && Number.isFinite(radius) && search.lat !== '' && search.lng !== '';

        fields.forEach(field => {
            const seenMembers = new Set<string>();
            for (const marker of facetUniverse) {
                const key = memberKey(marker);
                if (seenMembers.has(key)) continue;
                if (search.genre && marker.genre !== search.genre) continue;
                if (normalizedQuery) {
                    const searchable = [marker.prenom, marker.nom, marker.prenom_nom, marker.display_name, marker.nom_cabinet]
                        .map(value => String(value ?? '').toLocaleLowerCase())
                        .join(' ');
                    if (!searchable.includes(normalizedQuery)) continue;
                }
                if (hasGeoFilter) {
                    const markerLat = Number(marker.lat);
                    const markerLng = Number(marker.lng);
                    if (!Number.isFinite(markerLat) || !Number.isFinite(markerLng)
                        || distanceKm(searchLat, searchLng, markerLat, markerLng) > radius
                    ) continue;
                }
                const facets = marker.directory_facets;
                if (!facets) continue;
                const matchesOtherFields = fields.every(otherField => otherField === field
                    || search[otherField].length === 0
                    || search[otherField].some(slug => (facets[otherField] ?? []).includes(slug)));
                if (!matchesOtherFields) continue;

                seenMembers.add(key);
                const fieldCounts = counts.get(field) ?? new Map<string, number>();
                new Set(facets[field] ?? []).forEach(slug => fieldCounts.set(slug, (fieldCounts.get(slug) ?? 0) + 1));
                counts.set(field, fieldCounts);
            }
        });
        return counts;
    }, [facetUniverse, search]);
    const hasFacetPayload = useMemo(
        () => facetUniverse.some(marker => marker.directory_facets !== undefined),
        [facetUniverse],
    );

    // Conditions d'affichage des markers :
    // - si `show_markers_on_load` est true → toujours afficher
    // - sinon → afficher uniquement quand l'utilisateur a interagi
    //   avec la carte (pan/zoom) OU a fait une recherche active
    const showMarkers = settings.show_markers_on_load || userInteracted || searchActive;

    // Liste résultats panneau droit : membres dont les coordonnées tombent
    // dans la bbox du viewport (y compris points agrégés en cluster). Source =
    // `markers` (réponse REST), dédupliquée par memberKey (multi-adresses →
    // une carte par membre). Le plafonnement max_visible_results ne touche
    // que l'affichage, pas le décompte zone.
    const viewportMembers = useMemo(() => {
        if (!viewport || !showMarkers) {
            return { inViewport: [] as MarkerData[], displayed: [] as MarkerData[], inViewportCount: 0, displayedCount: 0 };
        }
        if (viewport.zoom < settings.list_results_min_zoom) {
            return { inViewport: [] as MarkerData[], displayed: [] as MarkerData[], inViewportCount: 0, displayedCount: 0 };
        }
        return selectViewportMembers(markers, viewport.bbox, {
            sortOrder: settings.sort_order,
            maxVisibleResults: settings.max_visible_results,
        });
    }, [markers, viewport, showMarkers, settings.list_results_min_zoom, settings.sort_order, settings.max_visible_results]);

    const visibleMembers = viewportMembers.displayed;
    const inViewportCount = viewportMembers.inViewportCount;

    // ── Chargement initial ─────────────────────────────────────────────
    const loadCachedMarkers = useCallback(async () => {
        setStatusMessage(texts['search_status_searching'] ?? t('membre.search.loading'));
        try {
            // La variante d'URL sépare aussi la clé CDN : une réponse publique
            // déjà au edge ne doit jamais masquer la variante membre privée.
            const data = await directoryGet<CachedMarkersResponse>(cfg, 'markers', {
                audience: isMember ? 'member' : 'public',
            });
            const mkrs = normalizeDirectoryMarkers(data.markers ?? [], countries);
            setMarkers(mkrs);
            setFacetUniverse(mkrs);
            // Compteur = thérapeutes uniques (pas une ligne par cabinet)
            setStatusCount(
                typeof data.count === 'number' && data.count >= 0
                    ? data.count
                    : countUniqueMembers(mkrs),
            );
            setStatusMessage('');
        } catch (err: unknown) {
            setStatusMessage(err instanceof Error && err.message ? err.message : t('membre.search.map_load_error'));
        }
    }, [cfg, countries, isMember, texts, t]);

    useEffect(() => {
        void loadCachedMarkers();
    }, [loadCachedMarkers]);

    // ── Recherche serveur ──────────────────────────────────────────────
    const performSearch = useCallback(async (
        s: SearchState,
        options: { preserveViewport?: boolean } = {},
    ) => {
        const preserveViewport = options.preserveViewport ?? false;
        setSearchActive(true);
        setStatusMessage(texts['search_status_searching'] ?? t('membre.search.searching'));
        try {
            const params: Record<string, string | string[]> = {
                search: s.query,
                genre: s.genre,
            };
            if (s.lat && s.lng) {
                params['lat'] = s.lat;
                params['lng'] = s.lng;
                params['radius'] = s.radius || '20';
            }
            if (s.langues.length) params['langue'] = s.langues;
            if (s.specialisations.length) params['specialisation'] = s.specialisations;
            if (s.champs_hypnose.length) params['champs_hypnose'] = s.champs_hypnose;
            if (s.publics_privilegies.length) params['publics_privilegies'] = s.publics_privilegies;
            if (s.pratiques_complementaires.length) params['pratiques_complementaires'] = s.pratiques_complementaires;

            const data = await directoryGet<SearchResultsResponse>(cfg, 'search', params);
            const mkrs = normalizeDirectoryMarkers(data.markers ?? [], countries);
            setMarkers(mkrs);
            const uniqueCount = typeof data.count === 'number' && data.count >= 0
                ? data.count
                : countUniqueMembers(mkrs);
            setStatusCount(uniqueCount);
            setStatusMessage('');

            // Analytics internes : recherche / filtres (fire-and-forget).
            trackDirectorySearch(cfg.rest_base_url, {
                pageContext: 'map',
                query: s.query,
                filters: {
                    genre: s.genre,
                    langues: s.langues,
                    specialisations: s.specialisations,
                    champs_hypnose: s.champs_hypnose,
                    publics_privilegies: s.publics_privilegies,
                    pratiques_complementaires: s.pratiques_complementaires,
                    lieu: s.lieu,
                    radius: s.lat && s.lng ? s.radius : '',
                },
                resultsCount: uniqueCount,
                source: 'search_bar',
            }, cfg.rest_nonce);

            // Un résultat unique n'a pas de bounds surfaciques. Publier une
            // petite vue déterministe évite que la liste reste bloquée sur
            // l'ancien viewport pendant (ou en l'absence de) moveEnd.
            if (!preserveViewport && mkrs.length === 1) {
                const lat = Number(mkrs[0].lat);
                const lng = Number(mkrs[0].lng);
                setViewport({
                    bbox: [lng - 0.1, lat - 0.1, lng + 0.1, lat + 0.1],
                    zoom: Math.max(settings.list_results_min_zoom, settings.marker_photo_min_zoom, 12),
                });
            }

            // Recadre la carte sur le centre de recherche si géoloc, sinon
            // sur les markers retournés.
            const map = mapRef.current?.getMap();
            if (map && !preserveViewport) {
                if (s.lat && s.lng) {
                    // Rayon serré → zoom plus fort (« près de moi » / 5 km ≈ quartier).
                    const geoZoom = s.radius === '5' ? 13 : s.radius === '10' ? 12 : 11;
                    map.flyTo({
                        center: [Number(s.lng), Number(s.lat)],
                        zoom: geoZoom,
                        duration: 700,
                        essential: true,
                    });
                    setViewport({
                        bbox: [
                            Number(s.lng) - 0.05,
                            Number(s.lat) - 0.04,
                            Number(s.lng) + 0.05,
                            Number(s.lat) + 0.04,
                        ],
                        zoom: geoZoom,
                    });
                } else if (mkrs.length === 1) {
                    map.easeTo({
                        center: [Number(mkrs[0].lng), Number(mkrs[0].lat)],
                        zoom: Math.max(settings.list_results_min_zoom, settings.marker_photo_min_zoom, 12),
                        duration: 400,
                    });
                } else if (mkrs.length > 1) {
                    let minLng = Number(mkrs[0].lng);
                    let maxLng = minLng;
                    let minLat = Number(mkrs[0].lat);
                    let maxLat = minLat;
                    for (const m of mkrs) {
                        const lng = Number(m.lng);
                        const lat = Number(m.lat);
                        if (lng < minLng) minLng = lng;
                        if (lng > maxLng) maxLng = lng;
                        if (lat < minLat) minLat = lat;
                        if (lat > maxLat) maxLat = lat;
                    }
                    map.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 50, maxZoom: 12, duration: 400 });
                }
            }
        } catch (err: unknown) {
            setStatusMessage(err instanceof Error && err.message ? err.message : t('membre.search.search_error'));
        }
    }, [cfg, countries, settings.list_results_min_zoom, settings.marker_photo_min_zoom, texts, t]);

    // ── Reset ──────────────────────────────────────────────────────────
    const handleReset = useCallback(() => {
        setSearch(INITIAL_SEARCH);
        setShowConfig(false);
        setConfigFocus(null);
        setSearchActive(false);
        setNameSuggestions([]);
        setShowNameSuggestions(false);
        setPlaceSuggestions([]);
        setShowPlaceSuggestions(false);
        setUserInteracted(false);
        clearNaturalSearch();
        setNaturalLocationError('');
        setFilterQuery('');
        setOpenFilterSections(['langues']);
        void loadCachedMarkers();
        const map = mapRef.current?.getMap();
        if (map) {
            map.fitBounds(homeBounds, { padding: 20, duration: 400 });
        }
    }, [loadCachedMarkers, homeBounds, clearNaturalSearch]);

    // ── Suggestions nom/cabinet + critères canoniques ─────────────────
    const onQueryChange = (val: string) => {
        setSearch(s => ({ ...s, query: val }));
        if (nameSuggestTimer.current) clearTimeout(nameSuggestTimer.current);
        const facetSuggestions = buildFacetSuggestions(val, filterOptions, suggestionGroupLabels);
        if (val.length < 2) {
            setShowNameSuggestions(false);
            setNameSuggestions([]);
            return;
        }
        setNameSuggestions(facetSuggestions);
        setShowNameSuggestions(facetSuggestions.length > 0);
        nameSuggestTimer.current = window.setTimeout(async () => {
            try {
                const data = await directoryGet<SuggestionsResponse>(cfg, 'suggestions', { search: val });
                if (Array.isArray(data)) {
                    const merged: DirectorySuggestionItem[] = [...facetSuggestions, ...data].slice(0, 10);
                    setNameSuggestions(merged);
                    setShowNameSuggestions(merged.length > 0);
                }
            } catch {
                setNameSuggestions(facetSuggestions);
                setShowNameSuggestions(facetSuggestions.length > 0);
            }
        }, 300);
    };

    const selectNameSuggestion = (sg: DirectorySuggestionItem) => {
        if (sg.type === 'filter') {
            const facet = sg as FacetSuggestionItem;
            const next = {
                ...search,
                query: '',
                [facet.field]: Array.from(new Set([...search[facet.field], facet.slug])),
            };
            setSearch(next);
            setNameSuggestions([]);
            setShowNameSuggestions(false);
            openCriteria(FILTER_SECTION_BY_FIELD[facet.field]);
            void performSearch(next, { preserveViewport: true });
            return;
        }
        const next = { ...search, query: sg.value };
        setSearch(next);
        setNameSuggestions([]);
        setShowNameSuggestions(false);
        void performSearch(next);
    };

    const keepSplitViewVisible = useCallback(() => {
        if (!isWideDirectoryLayout) return;
        const reposition = () => {
            const split = document.querySelector<HTMLElement>('.search-split-view');
            if (!split) return;

            const rect = split.getBoundingClientRect();
            const topMargin = 92;
            const bottomMargin = 24;
            const shouldMoveDown = rect.top < topMargin;
            const shouldMoveUp = rect.bottom > window.innerHeight - bottomMargin;
            if (!shouldMoveDown && !shouldMoveUp) return;

            const targetTop = Math.max(0, window.scrollY + rect.top - topMargin);
            window.scrollTo({ top: targetTop, behavior: 'auto' });
        };

        window.requestAnimationFrame(reposition);
        window.setTimeout(reposition, 0);
        window.setTimeout(reposition, 120);
    }, [isWideDirectoryLayout]);

    /**
     * Ouvre la capsule sur Préférences et déplie une section de critères
     * (spécialisations, langues…). Plus de rail à côté de la carte : tout
     * vit dans le panneau config (option 1 — accordion + search).
     */
    const openCriteria = (section?: string) => {
        if (section) {
            setOpenFilterSections(current => Array.from(new Set([...current, section])));
            setPendingFilterSection(section);
        }
        setConfigFocus('prefs');
        setShowConfig(true);
        keepSplitViewVisible();
    };

    const openConfig = (focus: 'who' | 'where' | 'prefs' | null = null) => {
        setConfigFocus(focus);
        setShowConfig(true);
    };

    const closeConfig = useCallback(() => {
        setShowConfig(false);
        setConfigFocus(null);
        setShowNameSuggestions(false);
        setShowPlaceSuggestions(false);
    }, []);

    // Clic hors drawer / hors barre capsule → fermer
    useEffect(() => {
        if (!showConfig) return undefined;
        const onPointerDown = (event: MouseEvent | TouchEvent) => {
            const target = event.target;
            if (!(target instanceof Node)) return;
            if (configPanelRef.current?.contains(target)) return;
            if (capsuleBarRef.current?.contains(target)) return;
            closeConfig();
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('touchstart', onPointerDown, { passive: true });
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('touchstart', onPointerDown);
        };
    }, [closeConfig, showConfig]);

    /**
     * gas-user-topbar (souvent sticky, z-index ~9800) + barre WP admin :
     * le drawer fixed top:0 passe dessous → on calcule le bas visible et on
     * décale top / max-height pour coller sous la topbar.
     */
    useEffect(() => {
        if (!showConfig) return undefined;

        const applyChromeOffset = () => {
            const panel = configPanelRef.current;
            if (!panel) return;

            // Mobile bottom-sheet : pas de top fixe sur grand écran uniquement
            const isMobileSheet = window.matchMedia('(max-width: 900px)').matches;
            if (isMobileSheet) {
                panel.style.removeProperty('top');
                panel.style.removeProperty('height');
                panel.style.removeProperty('max-height');
                panel.style.removeProperty('--gas-member-search-drawer-top');
                return;
            }

            let topPx = 0;
            const topbar = document.querySelector<HTMLElement>(
                '.gas-user-topbar:not(.gas-user-topbar--hidden-by-user):not(.gas-admin-topbar--hidden-by-user)',
            );
            if (topbar) {
                const style = window.getComputedStyle(topbar);
                if (style.display !== 'none' && style.visibility !== 'hidden') {
                    const rect = topbar.getBoundingClientRect();
                    if (rect.height > 0 && rect.bottom > 0) {
                        topPx = Math.max(topPx, Math.round(rect.bottom));
                    }
                }
            }
            const adminBar = document.getElementById('wpadminbar');
            if (adminBar) {
                const style = window.getComputedStyle(adminBar);
                if (style.display !== 'none') {
                    const rect = adminBar.getBoundingClientRect();
                    if (rect.height > 0 && rect.bottom > topPx) {
                        topPx = Math.round(rect.bottom);
                    }
                }
            }

            panel.style.setProperty('--gas-member-search-drawer-top', `${topPx}px`);
            panel.style.top = `${topPx}px`;
            panel.style.height = `calc(100dvh - ${topPx}px)`;
            panel.style.maxHeight = `calc(100dvh - ${topPx}px)`;
        };

        applyChromeOffset();
        // Layout topbar parfois après paint (menus, badges)
        const raf = window.requestAnimationFrame(applyChromeOffset);
        window.addEventListener('resize', applyChromeOffset);
        window.addEventListener('scroll', applyChromeOffset, { passive: true });
        return () => {
            window.cancelAnimationFrame(raf);
            window.removeEventListener('resize', applyChromeOffset);
            window.removeEventListener('scroll', applyChromeOffset);
        };
    }, [showConfig]);

    useEffect(() => {
        if (!showConfig || !pendingFilterSection) return undefined;

        const scrollToPendingSection = () => {
            const panel = filterPanelRefs.current.config;
            const section = panel?.querySelector<HTMLElement>(
                `.gas-member-search__filter-section--${pendingFilterSection}`,
            );
            // Le scroll vit dans .config-body (le panel lui-même overflow:hidden)
            const scrollContainer = configPanelRef.current
                ?.querySelector<HTMLElement>('.gas-member-search__config-body')
                ?? section?.closest<HTMLElement>('.gas-member-search__config-body')
                ?? panel;
            if (!section || !scrollContainer) return;

            // offsetTop relatif au conteneur de scroll (header sticky + barre mot-clé)
            let offsetTop = 0;
            let el: HTMLElement | null = section;
            while (el && el !== scrollContainer) {
                offsetTop += el.offsetTop;
                el = el.offsetParent instanceof HTMLElement ? el.offsetParent : null;
                // offsetParent peut sauter hors du panel : bascule sur getBoundingClientRect
                if (el && !scrollContainer.contains(el)) {
                    const sRect = section.getBoundingClientRect();
                    const cRect = scrollContainer.getBoundingClientRect();
                    offsetTop = scrollContainer.scrollTop + (sRect.top - cRect.top);
                    break;
                }
            }
            // Marge confortable sous header popup + search sticky (~header + sticky block)
            const stickyOffset = 132;
            const target = Math.max(0, offsetTop - stickyOffset);
            scrollContainer.scrollTo({ top: target, behavior: 'smooth' });
            section.querySelector<HTMLElement>('.gas-accordion__trigger')?.focus({ preventScroll: true });
        };

        // Double rAF + retry : l’accordéon doit finir d’ouvrir avant de mesurer
        let cancelled = false;
        const raf1 = window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                if (cancelled) return;
                scrollToPendingSection();
                window.setTimeout(() => {
                    if (!cancelled) scrollToPendingSection();
                    setPendingFilterSection(null);
                }, 200);
            });
        });
        return () => {
            cancelled = true;
            window.cancelAnimationFrame(raf1);
        };
    }, [openFilterSections, pendingFilterSection, showConfig]);

    // ── Suggestions lieu (MapTiler autocomplete) ───────────────────────
    const onLieuChange = (val: string) => {
        setSearch(s => ({ ...s, lieu: val }));
        if (placeSuggestTimer.current) clearTimeout(placeSuggestTimer.current);
        if (placeSuggestAbort.current) placeSuggestAbort.current.abort();
        if (val.length < 3) {
            setShowPlaceSuggestions(false);
            setPlaceSuggestions([]);
            return;
        }
        const controller = new AbortController();
        placeSuggestAbort.current = controller;
        placeSuggestTimer.current = window.setTimeout(async () => {
            try {
                const suggestions = await geocodeSearch({
                    query: val,
                    countries,
                    limit: 5,
                    maptilerApiKey: cfg.maptiler_api_key,
                    signal: controller.signal,
                });
                setPlaceSuggestions(suggestions);
                setShowPlaceSuggestions(suggestions.length > 0);
            } catch {
                /* abort/network — ignore */
            }
        }, 300);
    };

    const selectPlaceSuggestion = (suggestion: GasGeocodeSuggestion) => {
        const next: SearchState = {
            ...search,
            lat: String(suggestion.lat),
            lng: String(suggestion.lng),
            lieu: suggestion.label,
            radius: search.radius || '10',
        };
        setSearch(next);
        setPlaceSuggestions([]);
        setShowPlaceSuggestions(false);
        void performSearch(next);
    };

    // ── Géolocalisation « Près de moi » ────────────────────────────────
    // Mobile : enableHighAccuracy échoue souvent (code 2). On enchaîne
    // GPS précis → réseau/Wi‑Fi (moins précis mais plus disponible).
    const getBrowserPosition = useCallback(async (): Promise<GeolocationPosition> => {
        const once = (options: PositionOptions) => new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, options);
        });

        try {
            return await once({
                enableHighAccuracy: true,
                timeout: 8_000,
                maximumAge: 120_000,
            });
        } catch (first: unknown) {
            const code = (first as GeolocationPositionError | undefined)?.code;
            // Permission refusée : inutile de retenter.
            if (code === 1) {
                throw first;
            }
            // Code 2 (indisponible) / 3 (timeout) : repli sans GPS pur.
            return once({
                enableHighAccuracy: false,
                timeout: 15_000,
                maximumAge: 300_000,
            });
        }
    }, []);

    const searchAroundMe = async () => {
        if (typeof window !== 'undefined' && !window.isSecureContext) {
            setStatusMessage(t('membre.search.geo_insecure'));
            return;
        }
        if (!navigator.geolocation) {
            setStatusMessage(t('membre.search.geo_unavailable'));
            return;
        }
        setGeolocating(true);
        setStatusMessage(t('membre.search.locating'));
        try {
            const pos = await getBrowserPosition();
            const latNum = pos.coords.latitude;
            const lngNum = pos.coords.longitude;
            if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) {
                throw Object.assign(new Error('invalid_coords'), { code: 2 });
            }
            const lat = String(latNum);
            const lng = String(lngNum);
            const nearZoom = 13;
            const nearRadius = '5';

            // 1) Zoom carte tout de suite (ne pas attendre reverse-geocode / REST).
            setUserInteracted(true);
            const map = mapRef.current?.getMap();
            if (map) {
                map.flyTo({
                    center: [lngNum, latNum],
                    zoom: nearZoom,
                    duration: 900,
                    essential: true,
                });
            }
            setViewport({
                bbox: [lngNum - 0.04, latNum - 0.03, lngNum + 0.04, latNum + 0.03],
                zoom: nearZoom,
            });

            // 2) Libellé de lieu (best-effort, timeout court).
            let lieu = '';
            try {
                const reverse = reverseGeocode(latNum, lngNum);
                const timeout = new Promise<string>(resolve => {
                    window.setTimeout(() => resolve(''), 2500);
                });
                lieu = (await Promise.race([reverse, timeout])) || '';
            } catch {
                lieu = '';
            }
            if (!lieu.trim()) {
                lieu = t('membre.search.geo_coords_label', {
                    lat: latNum.toFixed(4),
                    lng: lngNum.toFixed(4),
                });
                if (lieu === 'membre.search.geo_coords_label') {
                    lieu = `${latNum.toFixed(4)}, ${lngNum.toFixed(4)}`;
                }
            }

            // 3) Recherche membres autour de moi (rayon 5 km).
            const next: SearchState = {
                ...search,
                lat,
                lng,
                lieu,
                radius: nearRadius,
            };
            setSearch(next);
            await performSearch(next);
        } catch (err: unknown) {
            const e = err as { code?: number; message?: string };
            let msg = t('membre.search.geo_impossible');
            if (e?.code === 1) msg = t('membre.search.geo_denied');
            else if (e?.code === 2) msg = t('membre.search.geo_unavailable');
            else if (e?.code === 3) msg = t('membre.search.geo_timeout');
            setStatusMessage(msg);
        } finally {
            setGeolocating(false);
        }
    };

    const preservePageScrollAfterFilter = useCallback((searchPromise?: Promise<unknown>) => {
        const left = window.scrollX;
        const top = window.scrollY;
        const restore = () => window.scrollTo(left, top);

        window.requestAnimationFrame(restore);
        window.setTimeout(restore, 0);

        if (searchPromise) {
            void searchPromise.finally(() => {
                restore();
                window.requestAnimationFrame(restore);
                window.setTimeout(restore, 120);
                window.setTimeout(restore, 600);
                window.setTimeout(restore, 1200);
            });
        }
    }, []);

    // ── Toggles filtres (déclenche une recherche immédiate) ────────────
    const onFilterToggle = (
        field: MultiFilterField,
        slug: string,
    ) => {
        const next = { ...search, [field]: toggleInArray(search[field], slug) };
        setSearch(next);
        const searchPromise = hasActiveFilters(next) || searchActive
            ? performSearch(next, { preserveViewport: true })
            : undefined;
        preservePageScrollAfterFilter(searchPromise);
    };

    const removeFilter = (chip: ActiveFilterChip) => {
        if (chip.field === 'genre') {
            onGenreChange('');
            return;
        }
        onFilterToggle(chip.field, chip.value);
    };

    const clearFacetFilters = () => {
        const next: SearchState = {
            ...search,
            genre: '',
            langues: [],
            specialisations: [],
            champs_hypnose: [],
            publics_privilegies: [],
            pratiques_complementaires: [],
        };
        setSearch(next);
        setFilterQuery('');
        clearNaturalSearch();
        setNaturalLocationError('');
        if (next.query || next.lieu || searchActive) void performSearch(next, { preserveViewport: true });
    };

    const runNaturalSearch = async (event?: React.FormEvent, forceAi = false) => {
        event?.preventDefault();
        const resolved = await naturalSearch.resolveText(naturalSearch.query, forceAi);
        if (!resolved) return;
        let next = mergeNlFilters(search, resolved.filters);
        const location = nlScalar(resolved.filters, 'location');
        const radius = nlScalar(resolved.filters, 'radius');
        setNaturalLocationError('');
        if (radius && ['5', '10', '20'].includes(radius)) {
            next = { ...next, radius };
        }
        if (location) {
            next = { ...next, lieu: location, lat: '', lng: '' };
            try {
                const [suggestion] = await geocodeSearch({
                    query: location,
                    countries,
                    limit: 1,
                    maptilerApiKey: cfg.maptiler_api_key,
                });
                if (suggestion) {
                    next = {
                        ...next,
                        lieu: suggestion.label,
                        lat: String(suggestion.lat),
                        lng: String(suggestion.lng),
                    };
                } else {
                    setNaturalLocationError(t('membre.search.nl.location_not_found', { location }));
                    setSearch(next);
                    // Lieu demandé mais non géocodable : ne pas élargir
                    // silencieusement à une recherche nationale.
                    return;
                }
            } catch {
                setNaturalLocationError(t('membre.search.nl.location_not_found', { location }));
                setSearch(next);
                return;
            }
        }
        setSearch(next);
        await performSearch(next, { preserveViewport: !location });
    };

    const onGenreChange = (value: string) => {
        const next = { ...search, genre: value };
        setSearch(next);
        if (hasActiveFilters(next) || searchActive) void performSearch(next, { preserveViewport: true });
    };

    const onRadiusChange = (value: string) => {
        const next = { ...search, radius: value };
        setSearch(next);
        if (next.lat && next.lng) void performSearch(next);
    };

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setShowNameSuggestions(false);
        setShowPlaceSuggestions(false);
        void performSearch(search);
    };

    // ── Map handlers ───────────────────────────────────────────────────
    // Une publication de viewport peut être causée par le cadrage initial.
    // Seuls les événements d'entrée réellement émis par le visiteur
    // déverrouillent donc les marqueurs masqués au chargement.
    const handleViewportChange = useCallback((v: GasGeoViewport) => {
        setViewport(v);
        (window as unknown as Record<string, unknown>).__memberSearchViewport = v;
    }, []);

    const handleMapInteraction = useCallback(() => {
        setUserInteracted(true);
    }, []);

    const handleMapLoad = useCallback((map: unknown) => {
        (window as unknown as Record<string, unknown>).__memberSearchMap = map;
    }, []);

    const handleMobileView = useCallback((next: 'map' | 'list') => {
        setMobileView(next);
        if (next === 'map') {
            window.requestAnimationFrame(() => mapRef.current?.getMap().resize());
        }
    }, []);

    /** Survol marqueur : highlight uniquement (ne touche pas à la fiche collante). */
    const handleMarkerHover = useCallback((id: string | null) => {
        if (id === null) {
            setHoveredId(null);
            return;
        }
        const point = pointById.get(id);
        // Clé membre (sync liste) — la carte reçoit l’id point via mapHoveredPointId.
        setHoveredId(point ? memberKey(point.data as MarkerData) : null);
    }, [pointById]);

    const openExpressCard = useCallback((member: MarkerData) => {
        setExpressMember(member);
        setHoveredId(memberKey(member));
    }, []);

    const closeExpressCard = useCallback(() => {
        setExpressMember(null);
    }, []);

    // Escape : fiche express, sinon panneau de configuration capsule.
    useEffect(() => {
        if (!expressMember && !showConfig) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            e.preventDefault();
            if (expressMember) {
                setExpressMember(null);
                return;
            }
            closeConfig();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [closeConfig, expressMember, showConfig]);

    // Focus le bon champ à l’ouverture du panneau capsule.
    useEffect(() => {
        if (!showConfig) return;
        const frame = window.requestAnimationFrame(() => {
            if (configFocus === 'where') {
                whereInputRef.current?.focus();
                return;
            }
            if (configFocus === 'prefs') {
                configPanelRef.current
                    ?.querySelector<HTMLElement>('[data-config-section="prefs"]')
                    ?.focus({ preventScroll: true });
                return;
            }
            whoInputRef.current?.focus();
        });
        return () => window.cancelAnimationFrame(frame);
    }, [configFocus, showConfig]);

    /** Id point carte (`memberKey#i`) pour hover popup + highlight marqueur. */
    const mapHoveredPointId = useMemo(() => {
        if (!hoveredId) return null;
        if (pointById.has(hoveredId)) return hoveredId;
        const match = mapPoints.find(p => memberKey(p.data as MarkerData) === hoveredId);
        return match?.id ?? null;
    }, [hoveredId, mapPoints, pointById]);

    const resolveMemberFacetLabels = useCallback((member: MarkerData): string[] => {
        const facets = member.directory_facets;
        if (!facets) return [];
        const labels: string[] = [];
        const pushField = (field: MultiFilterField) => {
            for (const slug of facets[field] ?? []) {
                const label = optionLabels.get(`${field}:${slug}`) ?? slug;
                if (label && !labels.includes(label)) labels.push(label);
            }
        };
        pushField('specialisations');
        pushField('langues');
        pushField('pratiques_complementaires');
        return labels;
    }, [optionLabels]);

    const resultsState = deriveDirectoryResultsState({
        showMarkers,
        viewportZoom: viewport?.zoom ?? null,
        listResultsMinZoom: settings.list_results_min_zoom,
        searchActive,
        markerCount: markers.length,
        visibleMemberCount: inViewportCount,
    });
    const showResults = resultsState === 'results';
    const showResetBtn = searchActive || hasActiveFilters(search);
    const normalizedFilterQuery = filterQuery.trim().toLocaleLowerCase();
    const filterVisible = (label: string) => !normalizedFilterQuery
        || label.toLocaleLowerCase().includes(normalizedFilterQuery);
    const filteredOptions = {
        langues: (filterOptions.langues ?? []).filter(option => filterVisible(option.label)),
        champs_hypnose: (filterOptions.champs_hypnose ?? []).filter(option => filterVisible(option.label)),
        publics_privilegies: (filterOptions.publics_privilegies ?? []).filter(option => filterVisible(option.label)),
        pratiques_complementaires: (filterOptions.pratiques_complementaires ?? []).filter(option => filterVisible(option.label)),
        specialisationsByCat: (filterOptions.specialisationsByCat ?? [])
            .map(category => ({ ...category, options: category.options.filter(option => filterVisible(option.label)) }))
            .filter(category => filterVisible(category.cat_label) || category.options.length > 0),
    };

    const allFilterSections = [
        filterOptions.specialisationsByCat?.length > 0 ? 'specialisations' : '',
        filterOptions.langues?.length > 0 ? 'langues' : '',
        filterOptions.publics_privilegies?.length > 0 ? 'publics' : '',
        filterOptions.champs_hypnose?.length > 0 ? 'champs' : '',
        filterOptions.pratiques_complementaires?.length > 0 ? 'pratiques' : '',
    ].filter(Boolean);
    const totalCriteria = (filterOptions.specialisationsByCat ?? []).reduce((total, category) => total + category.options.length, 0)
        + (filterOptions.langues ?? []).length
        + (filterOptions.publics_privilegies ?? []).length
        + (filterOptions.champs_hypnose ?? []).length
        + (filterOptions.pratiques_complementaires ?? []).length;
    const filterMatches = useMemo(
        () => buildFacetSuggestions(filterQuery, filterOptions, suggestionGroupLabels, 50)
            .filter((suggestion): suggestion is FacetSuggestionItem => suggestion.type === 'filter'),
        [filterOptions, filterQuery, suggestionGroupLabels],
    );
    const applyFacetSuggestion = (facet: FacetSuggestionItem) => {
        const next = {
            ...search,
            query: '',
            [facet.field]: Array.from(new Set([...search[facet.field], facet.slug])),
        };
        setSearch(next);
        setFilterQuery('');
        openCriteria(FILTER_SECTION_BY_FIELD[facet.field]);
        void performSearch(next, { preserveViewport: true });
    };

    const criteriaShortcuts = [
        { section: 'specialisations', label: texts['specializations_title'], available: filterOptions.specialisationsByCat?.length > 0 },
        { section: 'langues', label: texts['languages_title'], available: filterOptions.langues?.length > 0 },
        { section: 'champs', label: texts['fields_title'], available: filterOptions.champs_hypnose?.length > 0 },
        { section: 'publics', label: t('membre.search.ui.publics_title'), available: filterOptions.publics_privilegies?.length > 0 },
        { section: 'pratiques', label: t('membre.search.ui.pratiques_title'), available: filterOptions.pratiques_complementaires?.length > 0 },
    ].filter(item => item.available && Boolean(item.label));

    const whoSummary = search.query.trim() || t('membre.search.capsule.who_all');
    const whereSummary = (() => {
        const radiusLabel = search.radius
            ? t('membre.search.capsule.radius_km', { km: search.radius })
            : '';
        if (search.lieu.trim()) {
            return radiusLabel ? `${search.lieu.trim()} · ${radiusLabel}` : search.lieu.trim();
        }
        if (search.lat && search.lng) {
            const near = t('membre.search.capsule.near_me');
            return radiusLabel ? `${near} · ${radiusLabel}` : near;
        }
        return t('membre.search.capsule.where_any');
    })();
    const prefsSummary = activeFilterChips.length === 0
        ? t('membre.search.capsule.prefs_none')
        : activeFilterChips.length === 1
            ? t('membre.search.capsule.prefs_one')
            : t('membre.search.capsule.prefs_count', { count: String(activeFilterChips.length) });

    /** Libellé court du nombre de thérapeutes (capsule + popup). */
    const resultCountShort = statusCount === null
        ? null
        : statusCount === 1
            ? t('membre.search.capsule.results_one')
            : t('membre.search.capsule.results_many', { count: String(statusCount) });

    const applyConfigAndSearch = () => {
        closeConfig();
        setShowNameSuggestions(false);
        setShowPlaceSuggestions(false);
        void performSearch(search);
        if (window.matchMedia?.('(max-width: 900px)').matches) {
            handleMobileView('list');
        }
    };

    const filterPanelContent = (idSuffix: string) => (
        <div
            className="gas-member-search__filter-drawer gas-member-search__filter-drawer--in-config"
            ref={node => { filterPanelRefs.current[idSuffix] = node; }}
        >
            {/* Recherche par mot-clé (filtre les listes) — pas le NL */}
            <div className="gas-member-search__filter-search-block">
                <label className="gas-member-search__filter-search" htmlFor={`gas-member-directory-filter-search-${idSuffix}`}>
                    <span>{t('membre.search.filters.search_label')}</span>
                    <input
                        id={`gas-member-directory-filter-search-${idSuffix}`}
                        type="search"
                        value={filterQuery}
                        onChange={event => {
                            const value = event.target.value;
                            setFilterQuery(value);
                            // Dès 3 lettres : ouvrir les sections pour afficher les matches filtrés
                            if (value.trim().length >= 3) {
                                setOpenFilterSections(allFilterSections);
                            }
                        }}
                        placeholder={t('membre.search.filters.search_placeholder')}
                        autoComplete="off"
                    />
                </label>
                <p className="gas-member-search__filter-search-hint">
                    {t('membre.search.filters.search_keyword_help')}
                </p>
                {filterQuery.trim().length > 0 && filterQuery.trim().length < 3 && (
                    <p className="gas-member-search__filter-search-hint">{t('membre.search.filters.search_min_chars')}</p>
                )}
                {filterQuery.trim().length >= 3 && (
                    <div className="gas-member-search__filter-matches" aria-live="polite">
                        {filterMatches.length > 0 ? (
                            <>
                                <strong>{filterMatches.length === 1
                                    ? t('membre.search.filters.search_result_one')
                                    : t('membre.search.filters.search_results', { count: String(filterMatches.length) })}</strong>
                                <div>
                                    {filterMatches.map(match => (
                                        <button key={`${match.field}:${match.slug}`} type="button" onClick={() => applyFacetSuggestion(match)}>
                                            <span>{match.value}</span>
                                            <small>{match.groupLabel}</small>
                                        </button>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <p>{t('membre.search.filters.search_empty', { query: filterQuery.trim() })}</p>
                        )}
                    </div>
                )}
            </div>

            <div className="gas-member-search__quick-filters">
                <span>{t('membre.search.filters.gender')}</span>
                <button type="button" aria-pressed={!search.genre} onClick={() => onGenreChange('')}>
                    {texts['genre_all'] ?? t('membre.search.defaults.genre_all')}
                </button>
                <button type="button" aria-pressed={search.genre === 'femme'} onClick={() => onGenreChange(search.genre === 'femme' ? '' : 'femme')}>
                    {texts['genre_femme']}
                </button>
                <button type="button" aria-pressed={search.genre === 'homme'} onClick={() => onGenreChange(search.genre === 'homme' ? '' : 'homme')}>
                    {texts['genre_homme']}
                </button>
            </div>
            <div className="gas-member-search__criteria-toolbar">
                <span>{t('membre.search.filters.criteria_count', { count: String(totalCriteria) })}</span>
                {allFilterSections.length > 0 && (
                    <button
                        type="button"
                        onClick={() => {
                            const shouldCollapse = openFilterSections.length === allFilterSections.length;
                            if (!shouldCollapse) setFilterQuery('');
                            setOpenFilterSections(shouldCollapse ? [] : allFilterSections);
                        }}
                    >
                        {openFilterSections.length === allFilterSections.length
                            ? t('membre.search.filters.collapse_all')
                            : t('membre.search.filters.expand_all')}
                    </button>
                )}
            </div>
            {totalCriteria === 0 && (
                <p className="gas-member-search__criteria-empty">{t('membre.search.filters.criteria_unavailable')}</p>
            )}
            {/* Mise en page type rail : chips par catégorie (available-options / option-group) */}
            <GasAccordion type="multiple" value={openFilterSections} onValueChange={setOpenFilterSections}>
                {filterOptions.specialisationsByCat?.length > 0 && (
                    <GasAccordionItem value="specialisations" className="gas-member-search__filter-section gas-member-search__filter-section--specialisations" testId="member-filter-specialisations" header={texts['specializations_title']} badge={search.specialisations.length || undefined}>
                        <div className="gas-member-search__spec-scroll">
                            <SpecGroup cats={filteredOptions.specialisationsByCat} selected={search.specialisations} onToggle={v => onFilterToggle('specialisations', v)} counts={hasFacetPayload ? facetCounts.get('specialisations') : undefined} countTitle={count => t('membre.search.filters.count_current', { count: String(count) })} />
                        </div>
                    </GasAccordionItem>
                )}
                {filterOptions.langues?.length > 0 && (
                    <GasAccordionItem value="langues" className="gas-member-search__filter-section gas-member-search__filter-section--langues" header={texts['languages_title']} badge={search.langues.length || undefined}>
                        <OptionList options={filteredOptions.langues} selected={search.langues} onToggle={v => onFilterToggle('langues', v)} counts={hasFacetPayload ? facetCounts.get('langues') : undefined} countTitle={count => t('membre.search.filters.count_current', { count: String(count) })} />
                    </GasAccordionItem>
                )}
                {filterOptions.publics_privilegies?.length > 0 && (
                    <GasAccordionItem value="publics" className="gas-member-search__filter-section gas-member-search__filter-section--publics" header={t('membre.search.ui.publics_title')} badge={search.publics_privilegies.length || undefined}>
                        <OptionList options={filteredOptions.publics_privilegies} selected={search.publics_privilegies} onToggle={v => onFilterToggle('publics_privilegies', v)} counts={hasFacetPayload ? facetCounts.get('publics_privilegies') : undefined} countTitle={count => t('membre.search.filters.count_current', { count: String(count) })} />
                    </GasAccordionItem>
                )}
                {filterOptions.champs_hypnose?.length > 0 && (
                    <GasAccordionItem value="champs" className="gas-member-search__filter-section gas-member-search__filter-section--champs" header={texts['fields_title']} badge={search.champs_hypnose.length || undefined}>
                        <OptionList options={filteredOptions.champs_hypnose} selected={search.champs_hypnose} onToggle={v => onFilterToggle('champs_hypnose', v)} counts={hasFacetPayload ? facetCounts.get('champs_hypnose') : undefined} countTitle={count => t('membre.search.filters.count_current', { count: String(count) })} />
                    </GasAccordionItem>
                )}
                {filterOptions.pratiques_complementaires?.length > 0 && (
                    <GasAccordionItem value="pratiques" className="gas-member-search__filter-section gas-member-search__filter-section--pratiques" header={t('membre.search.ui.pratiques_title')} badge={search.pratiques_complementaires.length || undefined}>
                        <OptionList options={filteredOptions.pratiques_complementaires} selected={search.pratiques_complementaires} onToggle={v => onFilterToggle('pratiques_complementaires', v)} counts={hasFacetPayload ? facetCounts.get('pratiques_complementaires') : undefined} countTitle={count => t('membre.search.filters.count_current', { count: String(count) })} />
                    </GasAccordionItem>
                )}
            </GasAccordion>

            {/* NL en second plan : phrase libre, distinct de la recherche par mot-clé */}
            <details className="gas-member-search__natural-search gas-member-search__natural-search--collapsible">
                <summary>{t('membre.search.nl.label')}</summary>
                <div className="gas-member-search__natural-search-form" role="search">
                    <div className="gas-member-search__natural-search-bar">
                        <input
                            id={`gas-member-directory-natural-search-${idSuffix}`}
                            type="search"
                            value={naturalSearch.query}
                            onChange={event => naturalSearch.setQuery(event.target.value)}
                            onKeyDown={event => {
                                if (event.key !== 'Enter') return;
                                event.preventDefault();
                                void runNaturalSearch();
                            }}
                            placeholder={t('membre.search.nl.placeholder')}
                            aria-label={t('membre.search.nl.label')}
                        />
                        <GasButton
                            type="button"
                            variant="primary"
                            disabled={naturalSearch.loading || naturalSearch.query.trim().length < 2}
                            onClick={() => void runNaturalSearch()}
                        >
                            {naturalSearch.loading ? t('membre.search.nl.analyzing') : t('membre.search.nl.submit')}
                        </GasButton>
                    </div>
                    <p>{t('membre.search.nl.help')}</p>
                    {naturalSearch.error && <p className="gas-member-search__natural-search-error" role="alert">{naturalSearch.error}</p>}
                    {naturalLocationError && <p className="gas-member-search__natural-search-error" role="alert">{naturalLocationError}</p>}
                    {cfg.ai_refine_available === true
                        && naturalSearch.data?.unresolved?.length > 0
                        && naturalSearch.data.source !== 'ai'
                        && naturalSearch.data.source !== 'gate' && (
                        <GasButton
                            variant="secondary"
                            className="gas-member-search__natural-search-refine"
                            disabled={naturalSearch.loading}
                            onClick={() => void runNaturalSearch(undefined, true)}
                        >
                            {t('membre.search.nl.refine_ai')}
                        </GasButton>
                    )}
                </div>
            </details>
        </div>
    );

    return (
        <div
            className={`search-page-wrapper gas-member-search--density-${settings.card_density}`}
            data-testid="member-search-app"
            data-results-state={resultsState}
        >
            {/* ── Header ──────────────────────────────────────────────────── */}
            <div className="search-header">
                <h1 data-gas-field-key="site_texts.page_title">{texts['page_title']}</h1>
                {texts['main_description'] && (
                    <p className="search-description" data-gas-field-key="site_texts.main_description">{texts['main_description']}</p>
                )}
                {texts['professional_info'] && (
                    <p className="search-professional-info" data-gas-field-key="site_texts.professional_info">{texts['professional_info']}</p>
                )}
                {!isMember && texts['member_access_info'] && (
                    <p className="gas-info-recherche-membre-aph" data-gas-field-key="site_texts.member_access_info">{texts['member_access_info']}</p>
                )}
            </div>

            {/* ── Sticky capsule search (solution Airbnb) ─────────────────── */}
            <div className="search-bar-sticky gas-member-search__capsule-sticky">
                <form
                    className="search-form gas-member-search__capsule-form"
                    onSubmit={e => {
                        e.preventDefault();
                        applyConfigAndSearch();
                    }}
                >
                    <div className={`gas-member-search__capsule-wrap${showConfig ? ' is-open' : ''}`}>
                        <div
                            ref={capsuleBarRef}
                            className={`gas-member-search__capsule${showConfig ? ' is-open' : ''}${activeFilterChips.length > 0 ? ' has-filters' : ''}`}
                            role="search"
                            data-testid="member-search-capsule"
                        >
                            <button
                                type="button"
                                className={`gas-member-search__capsule-seg${configFocus === 'who' && showConfig ? ' is-focused' : ''}${search.query ? ' has-value' : ''}`}
                                onClick={() => (showConfig && configFocus === 'who' ? closeConfig() : openConfig('who'))}
                                aria-expanded={showConfig && configFocus === 'who'}
                                aria-controls="gas-member-search-config-panel"
                                aria-label={t('membre.search.capsule.segment_who_aria')}
                            >
                                <span className="gas-member-search__capsule-seg-label">{t('membre.search.capsule.who_label')}</span>
                                <span className="gas-member-search__capsule-seg-value">{whoSummary}</span>
                            </button>
                            <button
                                type="button"
                                className={`gas-member-search__capsule-seg${configFocus === 'where' && showConfig ? ' is-focused' : ''}${search.lieu || (search.lat && search.lng) ? ' has-value' : ''}`}
                                onClick={() => (showConfig && configFocus === 'where' ? closeConfig() : openConfig('where'))}
                                aria-expanded={showConfig && configFocus === 'where'}
                                aria-controls="gas-member-search-config-panel"
                                aria-label={t('membre.search.capsule.segment_where_aria')}
                            >
                                <span className="gas-member-search__capsule-seg-label">{t('membre.search.capsule.where_label')}</span>
                                <span className="gas-member-search__capsule-seg-value">{whereSummary}</span>
                            </button>
                            <button
                                type="button"
                                className={`gas-member-search__capsule-seg${configFocus === 'prefs' && showConfig ? ' is-focused' : ''}${activeFilterChips.length > 0 ? ' has-value' : ''}`}
                                onClick={() => (showConfig && configFocus === 'prefs' ? closeConfig() : openConfig('prefs'))}
                                aria-expanded={showConfig && configFocus === 'prefs'}
                                aria-controls="gas-member-search-config-panel"
                                aria-label={t('membre.search.capsule.segment_prefs_aria')}
                            >
                                <span className="gas-member-search__capsule-seg-label">{t('membre.search.capsule.prefs_label')}</span>
                                <span className="gas-member-search__capsule-seg-value">{prefsSummary}</span>
                                {activeFilterChips.length > 0 && (
                                    <strong className="gas-member-search__filter-count">{activeFilterChips.length}</strong>
                                )}
                            </button>
                            <button
                                type="submit"
                                className="gas-member-search__capsule-go search-button"
                                aria-label={
                                    statusCount !== null
                                        ? t('membre.search.capsule.search_aria_with_count', { count: String(statusCount) })
                                        : t('membre.search.capsule.search_aria')
                                }
                                title={texts['search_button'] ?? t('membre.search.defaults.search_button')}
                            >
                                <span className="gas-member-search__capsule-go-label">
                                    {texts['search_button'] ?? t('membre.search.defaults.search_button')}
                                </span>
                                {statusCount !== null && (
                                    <span className="gas-member-search__capsule-go-count" aria-hidden="true">
                                        {statusCount}
                                    </span>
                                )}
                                <span className="gas-member-search__capsule-go-icon" aria-hidden="true">→</span>
                            </button>
                        </div>

                        {resultCountShort && (
                            <p className="gas-member-search__result-count" data-testid="member-search-result-count" aria-live="polite">
                                {resultCountShort}
                            </p>
                        )}
                    </div>

                    {showConfig && (
                        <>
                            <button
                                type="button"
                                className="gas-member-search__config-backdrop"
                                aria-label={t('membre.search.capsule.close')}
                                onClick={closeConfig}
                                data-testid="member-search-config-backdrop"
                            />
                            <div
                                id="gas-member-search-config-panel"
                                ref={configPanelRef}
                                className="gas-member-search__config-panel gas-member-search__config-panel--drawer"
                                role="dialog"
                                aria-modal="true"
                                aria-label={t('membre.search.capsule.config_title')}
                                data-testid="member-search-config-panel"
                            >
                                <header className="gas-member-search__config-panel-header">
                                    <h2>{t('membre.search.capsule.config_title')}</h2>
                                    <div className="gas-member-search__config-panel-header-actions">
                                        {statusCount !== null && (
                                            <span className="gas-member-search__config-result-count" data-testid="member-search-config-result-count">
                                                {statusCount === 1
                                                    ? t('membre.search.result_one')
                                                    : t('membre.search.result_many', { count: String(statusCount) })}
                                            </span>
                                        )}
                                        <button
                                            type="button"
                                            className="gas-member-search__config-close"
                                            onClick={closeConfig}
                                            aria-label={t('membre.search.capsule.close')}
                                        >
                                            <span aria-hidden="true">×</span>
                                        </button>
                                    </div>
                                </header>

                                <div className="gas-member-search__config-body">
                                <div className="gas-member-search__config-grid">
                                    <section
                                        className={`gas-member-search__config-section${configFocus === 'who' ? ' is-focused' : ''}`}
                                        data-config-section="who"
                                    >
                                        <h3>{t('membre.search.capsule.section_who')}</h3>
                                        <div className="search-input-container">
                                            <input
                                                ref={whoInputRef}
                                                type="text"
                                                className="search-input"
                                                placeholder={texts['search_placeholder']}
                                                value={search.query}
                                                onChange={e => onQueryChange(e.target.value)}
                                                onFocus={() => {
                                                    setConfigFocus('who');
                                                    if (nameSuggestions.length > 0) setShowNameSuggestions(true);
                                                }}
                                                onBlur={() => setTimeout(() => setShowNameSuggestions(false), 200)}
                                                autoComplete="off"
                                            />
                                            {showNameSuggestions && nameSuggestions.length > 0 && (
                                                <div className="search-suggestions gas-member-search__suggestions--open">
                                                    {nameSuggestions.map(sg => (
                                                        <div
                                                            key={sg.value + sg.type}
                                                            className="suggestion-item"
                                                            onMouseDown={e => {
                                                                e.preventDefault();
                                                                selectNameSuggestion(sg);
                                                                if (sg.type !== 'filter') closeConfig();
                                                            }}
                                                        >
                                                            <span className="suggestion-type">
                                                                {sg.type === 'filter'
                                                                    ? t('membre.search.filters.suggestion_filter', { group: sg.groupLabel })
                                                                    : sg.type === 'cabinet'
                                                                    ? t('membre.search.label_cabinet')
                                                                    : t('membre.search.label_nom')}
                                                            </span>
                                                            <div className="suggestion-content">
                                                                <div className="suggestion-title">{sg.value}</div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="gas-member-search__config-section-actions">
                                            <GasButton
                                                type="button"
                                                variant="secondary"
                                                size="s"
                                                onClick={handleReset}
                                                data-testid="member-search-reset-under-who"
                                            >
                                                {t('membre.search.capsule.reset_filters')}
                                            </GasButton>
                                            {activeFilterChips.length > 0 && (
                                                <GasButton
                                                    type="button"
                                                    variant="ghost"
                                                    size="s"
                                                    onClick={clearFacetFilters}
                                                >
                                                    {t('membre.search.filters.clear')}
                                                </GasButton>
                                            )}
                                        </div>
                                    </section>

                                    <section
                                        className={`gas-member-search__config-section${configFocus === 'where' ? ' is-focused' : ''}`}
                                        data-config-section="where"
                                    >
                                        <h3>{t('membre.search.capsule.section_where')}</h3>
                                        <div className="location-group gas-member-search__config-location">
                                            <div className="gas-member-search__location-field">
                                                <input
                                                    ref={whereInputRef}
                                                    type="text"
                                                    className="location-input"
                                                    placeholder={texts['location_placeholder']}
                                                    value={search.lieu}
                                                    onChange={e => onLieuChange(e.target.value)}
                                                    onFocus={() => setConfigFocus('where')}
                                                    onBlur={() => setTimeout(() => setShowPlaceSuggestions(false), 200)}
                                                    autoComplete="off"
                                                />
                                                {showPlaceSuggestions && placeSuggestions.length > 0 && (
                                                    <div className="search-suggestions gas-member-search__suggestions--open">
                                                        {placeSuggestions.map(suggestion => (
                                                            <div
                                                                key={suggestion.id}
                                                                className="suggestion-item"
                                                                onMouseDown={e => {
                                                                    e.preventDefault();
                                                                    selectPlaceSuggestion(suggestion);
                                                                }}
                                                            >
                                                                <div className="suggestion-content">
                                                                    <div className="suggestion-title">{suggestion.label}</div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            {hasGeolocation && (
                                                <div className="search-around-me">
                                                    <button
                                                        type="button"
                                                        className={`search-around-me-button${geolocating ? ' is-loading' : ''}`}
                                                        disabled={geolocating}
                                                        onClick={() => void searchAroundMe()}
                                                        aria-label={geolocating
                                                            ? t('membre.search.locating')
                                                            : (texts['here_button'] ?? t('membre.search.defaults.here_button'))}
                                                        title={texts['here_button'] ?? t('membre.search.defaults.here_button')}
                                                    >
                                                        {geolocating
                                                            ? <IconLoader className="search-around-me-button__icon is-spinning" />
                                                            : <IconCurrentLocation className="search-around-me-button__icon" />}
                                                        <span className="search-around-me-button__label">
                                                            {geolocating
                                                                ? t('membre.search.defaults.here_loading')
                                                                : (texts['here_button'] ?? t('membre.search.defaults.here_button'))}
                                                        </span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        <div className="gas-member-search__radius-block">
                                            <span className="gas-member-search__radius-title">
                                                {t('membre.search.capsule.radius_title')}
                                            </span>
                                            <div className="gas-member-search__radius-options" role="group" aria-label={t('membre.search.capsule.radius_title')}>
                                                {(['5', '10', '20'] as const).map(km => (
                                                    <button
                                                        key={km}
                                                        type="button"
                                                        className={search.radius === km ? 'is-active' : ''}
                                                        aria-pressed={search.radius === km}
                                                        onClick={() => onRadiusChange(km)}
                                                    >
                                                        {t('membre.search.capsule.radius_km', { km })}
                                                    </button>
                                                ))}
                                            </div>
                                            <p className="gas-member-search__field-help">
                                                {t('membre.search.capsule.radius_help')}
                                            </p>
                                        </div>
                                    </section>

                                    <section
                                        className={`gas-member-search__config-section gas-member-search__config-section--prefs${configFocus === 'prefs' ? ' is-focused' : ''}`}
                                        data-config-section="prefs"
                                        tabIndex={-1}
                                    >
                                        <div className="gas-member-search__config-prefs-head">
                                            <h3>{t('membre.search.capsule.section_prefs')}</h3>
                                            {criteriaShortcuts.length > 0 && (
                                                <div
                                                    className="gas-member-search__criteria-shortcuts gas-member-search__config-shortcuts"
                                                    role="toolbar"
                                                    aria-label={t('membre.search.filters.explore_hint')}
                                                >
                                                    {criteriaShortcuts.map(item => (
                                                        <button
                                                            key={item.section}
                                                            type="button"
                                                            className={openFilterSections.includes(item.section) ? 'is-active' : ''}
                                                            aria-pressed={openFilterSections.includes(item.section)}
                                                            onClick={() => openCriteria(item.section)}
                                                        >
                                                            {item.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        {/* Critères complets in-popup (plus de rail carte / drawer) */}
                                        <div data-testid="member-search-open-filters" className="gas-member-search__config-criteria">
                                            {filterPanelContent('config')}
                                        </div>
                                    </section>
                                </div>
                                </div>

                                <footer className="gas-member-search__config-footer">
                                    <button
                                        type="button"
                                        className="gas-button gas-button--secondary"
                                        onClick={handleReset}
                                    >
                                        {t('membre.search.ui.reset')}
                                    </button>
                                    <button
                                        type="button"
                                        className="gas-button gas-button--primary"
                                        onClick={applyConfigAndSearch}
                                    >
                                        {t('membre.search.capsule.apply')}
                                        {statusCount !== null ? ` (${statusCount})` : ''}
                                    </button>
                                </footer>
                            </div>
                        </>
                    )}

                    {cfg.public_members_list_enabled && cfg.public_members_list_url && (
                        <p className="gas-member-search__list-format">
                            <i className="ti ti-list" aria-hidden="true" />
                            <span>{t('membre.search.ui.list_format_hint')}</span>
                            <a href={cfg.public_members_list_url}>{t('membre.search.ui.list_format_link')}</a>
                        </p>
                    )}

                    {(statusCount !== null || statusMessage) && (
                        <div className="gas-search-status" aria-live="polite">
                            {statusMessage || (!showMarkers
                                ? t('membre.search.ui.initial_state')
                                : (() => {
                                if (viewport && viewport.zoom >= settings.list_results_min_zoom && statusCount !== null) {
                                    if (
                                        settings.max_visible_results > 0
                                        && inViewportCount > settings.max_visible_results
                                    ) {
                                        return t('membre.search.filters.viewport_count_capped', {
                                            visible: String(inViewportCount),
                                            shown: String(visibleMembers.length),
                                            total: String(statusCount),
                                        });
                                    }
                                    return t('membre.search.filters.viewport_count', {
                                        visible: String(inViewportCount),
                                        total: String(statusCount),
                                    });
                                }
                                if (statusCount === 1) {
                                    return t('membre.search.result_one');
                                }
                                return t('membre.search.result_many', { count: String(statusCount) });
                                })())}
                        </div>
                    )}

                    {(activeFilterChips.length > 0 || showResetBtn) && (
                        <div className="gas-member-search__active-filters-row">
                            {activeFilterChips.length > 0 && (
                                <div className="gas-member-search__active-filters" aria-label={t('membre.search.filters.active_label')}>
                                    {activeFilterChips.map(chip => (
                                        <button key={chip.key} type="button" onClick={() => removeFilter(chip)}>
                                            <span>{chip.label}</span><span aria-hidden="true">×</span>
                                        </button>
                                    ))}
                                    <button type="button" className="gas-member-search__clear-filters" onClick={clearFacetFilters}>
                                        {t('membre.search.filters.clear')}
                                    </button>
                                </div>
                            )}
                            {showResetBtn && (
                                <GasButton variant="ghost" size="s" className="gas-member-search__reset" onClick={handleReset}>
                                    <i className="ti ti-x" aria-hidden="true" />
                                    {t('membre.search.ui.reset')}
                                </GasButton>
                            )}
                        </div>
                    )}
                </form>
            </div>

            <div
                className="gas-member-search__mobile-view-switch"
                role="group"
                aria-label={t('membre.search.ui.mobile_view_label')}
                data-testid="member-search-mobile-view-switch"
            >
                <button
                    type="button"
                    className={mobileView === 'map' ? 'is-active' : ''}
                    aria-pressed={mobileView === 'map'}
                    onClick={() => handleMobileView('map')}
                >
                    <i className="ti ti-map-2" aria-hidden="true" />
                    {t('membre.search.ui.mobile_map')}
                </button>
                <button
                    type="button"
                    className={mobileView === 'list' ? 'is-active' : ''}
                    aria-pressed={mobileView === 'list'}
                    onClick={() => handleMobileView('list')}
                >
                    <i className="ti ti-list" aria-hidden="true" />
                    {t('membre.search.ui.mobile_list', { count: String(inViewportCount) })}
                </button>
            </div>

            {/* ── Split view (carte + liste uniquement — filtres dans la capsule) ─ */}
            <div className="search-split-view" data-mobile-view={mobileView}>
                <div className="search-map-panel">
                    <div
                        className="gas-vue-map-wrapper"
                        onPointerDownCapture={handleMapInteraction}
                        onWheelCapture={handleMapInteraction}
                        onKeyDownCapture={handleMapInteraction}
                    >
                        <GasGeoMap<MarkerData>
                            points={showMarkers ? mapPoints : []}
                            styleUrl={cfg.map_style_url}
                            darkStyleUrl={cfg.map_style_url_dark}
                            mapRef={mapRef}
                            initialBounds={homeBounds}
                            fitPadding={20}
                            cluster={mapPoints.length > 1 ? {
                                radius: Math.max(20, settings.cluster_radius || 40),
                                maxZoom: Math.max(6, settings.cluster_max_zoom || 10),
                                minPoints: Math.max(2, settings.cluster_min_points || 3),
                            } : false}
                            hoveredId={mapHoveredPointId}
                            onHoverChange={handleMarkerHover}
                            onViewportChange={handleViewportChange}
                            onLoad={handleMapLoad}
                            renderMarker={point => {
                                const member = point.data as MarkerData;
                                const id = memberKey(member);
                                const isHover = hoveredId === id;

                                // Numérotation stable, indépendante du seuil de
                                // visibilité de la liste et des clusters.
                                const number = memberOrder.get(id);
                                const showPhoto = settings.marker_style === 'photo_zoom'
                                    && (viewport?.zoom ?? 0) >= settings.marker_photo_min_zoom;
                                const isNumbered = settings.marker_style === 'numbered' && number !== undefined;

                                if (showPhoto) {
                                    return (
                                        <span className={`gas-member-search__marker-photo${isHover ? ' gas-member-search__marker-photo--hover' : ''}`}>
                                            <MemberAvatar member={member} alt="" />
                                        </span>
                                    );
                                }
                                if (isNumbered) {
                                    return (
                                        <span className={`gas-member-search__marker-number${isHover ? ' gas-member-search__marker-number--hover' : ''}`}>
                                            {number}
                                        </span>
                                    );
                                }
                                return (
                                    <span className={`gas-member-search__marker-dot${isHover ? ' gas-member-search__marker-dot--hover' : ''}`} />
                                );
                            }}
                            // Fiche express collante au clic (overlay DOM, pas Popup MapLibre).
                            onPointClick={point => {
                                const member = point.data as MarkerData;
                                const mid = Number(member.member_id ?? 0);
                                if (mid > 0) {
                                    trackMemberClick(cfg.rest_base_url, {
                                        memberId: mid,
                                        source: 'marker_popup',
                                        pageContext: 'map',
                                    }, cfg.rest_nonce);
                                }
                                openExpressCard(member);
                            }}
                        />
                    </div>
                    {/* Couche d’overlay = même boîte que le panneau carte (contexte de positionnement sûr). */}
                    <div className="search-map-panel__overlay" data-testid="member-search-map-overlay">
                        {expressMember && (
                            <div
                                className="gas-member-search__express-card"
                                role="dialog"
                                aria-modal="false"
                                aria-label={getFullName(expressMember)}
                                data-testid="member-search-express-card"
                            >
                                <button
                                    type="button"
                                    className="gas-member-search__express-card-close"
                                    aria-label={t('common.close')}
                                    onClick={closeExpressCard}
                                >
                                    ×
                                </button>
                                <MemberMapSummary
                                    member={expressMember}
                                    facetLabels={resolveMemberFacetLabels(expressMember)}
                                    profileLabel={t('membre.search.ui.view_profile')}
                                    onProfileClick={(m) => {
                                        const mid = Number(m.member_id ?? 0);
                                        if (mid > 0) {
                                            trackMemberClick(cfg.rest_base_url, {
                                                memberId: mid,
                                                source: 'marker_popup',
                                                pageContext: 'map',
                                            }, cfg.rest_nonce);
                                        }
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="search-results-panel">
                    <div className="search-results-panel-inner">
                        {showResults ? (
                            <>
                                <h3 className="results-title">{texts['results_title']}</h3>
                                <div className="gas-member-results-wrapper">
                                    <div className="results-grid">
                                        {visibleMembers.map(m => {
                                            const id = memberKey(m);
                                            const number = memberOrder.get(id);
                                            const isExpress = expressMember
                                                ? memberKey(expressMember) === id
                                                : false;
                                            return (
                                                <div
                                                    key={id}
                                                    className={[
                                                        'result-item',
                                                        hoveredId === id ? 'result-item--hover' : '',
                                                        isExpress ? 'result-item--express' : '',
                                                    ].filter(Boolean).join(' ')}
                                                    onMouseEnter={() => setHoveredId(id)}
                                                    onMouseLeave={() => setHoveredId(null)}
                                                    onClick={(e) => {
                                                        // Liens profil restent navigables ; clic hors lien = fiche collante.
                                                        if ((e.target as HTMLElement).closest('a, button')) {
                                                            return;
                                                        }
                                                        openExpressCard(m);
                                                    }}
                                                >
                                                    <div className="result-image">
                                                        <a
                                                            href={String(m.aph_member_url ?? '#')}
                                                            onClick={() => {
                                                                const mid = Number(m.member_id ?? 0);
                                                                if (mid > 0) {
                                                                    trackMemberClick(cfg.rest_base_url, {
                                                                        memberId: mid,
                                                                        source: 'map_card',
                                                                        pageContext: 'map',
                                                                    }, cfg.rest_nonce);
                                                                }
                                                            }}
                                                        >
                                                            <MemberAvatar member={m} alt={getFullName(m)} />
                                                        </a>
                                                    </div>
                                                    <div className="result-details">
                                                        {settings.marker_style === 'numbered' && number !== undefined && (
                                                            <span className="gas-member-search__result-number" aria-hidden="true">
                                                                {number}
                                                            </span>
                                                        )}
                                                        {m.nom_cabinet && (
                                                            <h3>
                                                                <a
                                                                    href={String(m.aph_member_url ?? '#')}
                                                                    onClick={() => {
                                                                        const mid = Number(m.member_id ?? 0);
                                                                        if (mid > 0) {
                                                                            trackMemberClick(cfg.rest_base_url, {
                                                                                memberId: mid,
                                                                                source: 'map_card',
                                                                                pageContext: 'map',
                                                                            }, cfg.rest_nonce);
                                                                        }
                                                                    }}
                                                                >
                                                                    {m.nom_cabinet}
                                                                </a>
                                                            </h3>
                                                        )}
                                                        <p className="result-name">
                                                            <a
                                                                href={String(m.aph_member_url ?? '#')}
                                                                onClick={() => {
                                                                    const mid = Number(m.member_id ?? 0);
                                                                    if (mid > 0) {
                                                                        trackMemberClick(cfg.rest_base_url, {
                                                                            memberId: mid,
                                                                            source: 'map_card',
                                                                            pageContext: 'map',
                                                                        }, cfg.rest_nonce);
                                                                    }
                                                                }}
                                                            >
                                                                {getFullName(m)}
                                                            </a>
                                                        </p>
                                                        {activeFilterChips.length > 0 && (
                                                            <div
                                                                className="gas-member-search__match-badges"
                                                                aria-label={t('membre.search.filters.active_criteria')}
                                                            >
                                                                {activeFilterChips.slice(0, 3).map(chip => (
                                                                    <span key={chip.key}>{chip.label}</span>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {getStreetAddress(m) && (
                                                            <p className="result-address">{getStreetAddress(m)}</p>
                                                        )}
                                                        {getCityLine(m) && (
                                                            <p className="result-address result-address-city">{getCityLine(m)}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="search-results-empty-state" data-state={resultsState}>
                                <div className="empty-icon" aria-hidden="true">
                                    {resultsState === 'zoom' || resultsState === 'pending-viewport'
                                        ? '＋'
                                        : resultsState === 'initial'
                                            ? '⌖'
                                            : '🔍'}
                                </div>
                                <p>
                                    {resultsState === 'initial'
                                        ? t('membre.search.ui.initial_state')
                                        : resultsState === 'pending-viewport'
                                            ? t('membre.search.ui.pending_viewport_state')
                                            : resultsState === 'zoom'
                                                ? t('membre.search.ui.zoom_state')
                                                : t('membre.search.ui.empty_state')}
                                </p>
                                <p className="empty-hint">
                                    {resultsState === 'initial'
                                        ? t('membre.search.ui.initial_hint')
                                        : resultsState === 'pending-viewport'
                                            ? t('membre.search.ui.pending_viewport_hint')
                                            : resultsState === 'zoom'
                                                ? t('membre.search.ui.zoom_hint')
                                                : t('membre.search.ui.empty_hint')}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Landings SEO locales — sous la carte pour ne pas prendre la place de la barre de recherche */}
            {cfg.local_seo_enabled && (cfg.local_seo_places?.length ?? 0) > 0 && (
                <nav className="gas-member-search__local-places" aria-label={t('membre.local_seo.nav_label')}>
                    {(['city', 'canton'] as const).map(kind => {
                        const places = (cfg.local_seo_places ?? []).filter(p => (p.kind ?? 'city') === kind);
                        if (places.length === 0) return null;
                        const indexUrl = kind === 'canton'
                            ? cfg.local_seo_canton_index_url
                            : cfg.local_seo_index_url;
                        return (
                            <div key={kind} className="gas-member-search__local-places-block">
                                <div className="gas-member-search__local-places-head">
                                    <span className="gas-member-search__local-places-title">
                                        {t(kind === 'canton' ? 'membre.local_seo.nav_cantons' : 'membre.local_seo.nav_title')}
                                    </span>
                                    {indexUrl && (
                                        <a className="gas-member-search__local-places-all" href={indexUrl}>
                                            {t(kind === 'canton' ? 'membre.local_seo.nav_all_cantons' : 'membre.local_seo.nav_all')}
                                        </a>
                                    )}
                                </div>
                                <ul className="gas-member-search__local-places-list">
                                    {places.map(place => (
                                        <li key={`${kind}-${place.slug}`}>
                                            <a href={place.url}>{place.label}</a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })}
                </nav>
            )}
        </div>
    );
};

export default App;
